/**
 * EPIC AUTH strings, Arabic (the source language).
 *
 * Copy is transcribed from `design/MedOSAuthPublic.dc.html` where the design
 * has it, and written in the same register where it does not (reset completion,
 * the idle warning, the recovery-code sheet).
 */
export const authAr: Record<string, string> = {
  // --- Marketing panel -----------------------------------------------------
  //
  // Word for word from the landing page (`landing-page/src/App.tsx`): the
  // headline is its `<h1>`, the body its lead, and the three claims are the
  // trust chip and two of the «ما لا تجده في غيره» items. Someone arriving here
  // from clinvisor.com has just read those sentences, and a login screen that
  // paraphrased them read as a different product wearing the same logo.
  //
  // If the landing page's hero changes, change these with it.
  'auth.marketing.headline': 'من الحجز إلى التحصيل',
  'auth.marketing.body': 'نظام عربي واحد لإدارة العيادة: المواعيد، الملف الطبي، المختبر، والفوترة.',
  'auth.marketing.claim1': 'بيانات كل عيادة معزولة',
  'auth.marketing.claim2': 'السجل المثبت لا يُعدَّل',
  'auth.marketing.claim3': 'فحص الدواء لا يعتمد على الذكاء الاصطناعي',
  // The landing page's eyebrow, verbatim — it says who the product is for
  // rather than what it is not, which is the more useful thing on a login
  // screen. The "not a hospital information system" scope limit it replaced
  // still stands and is still stated, in the landing page's FAQ.
  'auth.marketing.footnote': 'للعيادات والمراكز الطبية — من طبيب واحد إلى عدة فروع',

  // --- Shared fields -------------------------------------------------------
  'auth.field.email': 'البريد الإلكتروني',
  'auth.field.password': 'كلمة المرور',

  // --- Login ---------------------------------------------------------------
  'auth.login.title': 'تسجيل الدخول',
  'auth.login.subtitle': 'أدخل بيانات حسابك للمتابعة إلى مساحة العيادة.',
  'auth.login.forgot': 'نسيت كلمة المرور؟',
  'auth.login.submit': 'دخول',
  'auth.login.busy': 'جارٍ التحقق من البيانات…',
  'auth.login.needsBoth': 'أدخل البريد الإلكتروني وكلمة المرور',
  'auth.login.noClinic': 'عيادتك ليست مسجلة بعد؟',
  'auth.login.registerClinic': 'سجّل عيادة جديدة',
  // One message for every credential failure: an unknown address and a wrong
  // password must be indistinguishable.
  'auth.login.error': 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
  'auth.login.rateLimited': 'محاولات كثيرة خلال وقت قصير. انتظر دقيقة ثم أعد المحاولة.',

  // --- MFA challenge -------------------------------------------------------
  'auth.mfa.title': 'التحقق بخطوتين',
  'auth.mfa.subtitle': 'أدخل الرمز المكوّن من ٦ أرقام من تطبيق المصادقة الخاص بك.',
  'auth.mfa.codeLabel': 'رمز التحقق',
  'auth.mfa.submit': 'تحقق ودخول',
  'auth.mfa.needsCode': 'أدخل الرمز كاملاً',
  'auth.mfa.useRecovery': 'استخدام رمز استرداد',
  'auth.mfa.useApp': 'العودة إلى رمز التطبيق',
  'auth.mfa.recoveryLabel': 'رمز الاسترداد',
  'auth.mfa.recoveryHint': 'أحد الرموز التي حفظتها عند تفعيل التحقق بخطوتين. يُستخدم مرة واحدة.',
  'auth.mfa.attemptsRemaining': '{count} محاولات متبقية',
  'auth.mfa.wrongCode': 'الرمز غير صحيح. تحقق من الوقت في جهازك ثم أعد المحاولة.',
  'auth.mfa.exhausted': 'استُنفدت المحاولات. سجّل الدخول مرة أخرى.',
  'auth.mfa.back': 'العودة لتسجيل الدخول',

  // --- Forced enrolment ----------------------------------------------------
  'auth.mfa.enrolTitle': 'التحقق بخطوتين مطلوب',
  'auth.mfa.enrolSubtitle':
    'تشترط منشأتك التحقق بخطوتين لدورك. امسح رمز الإعداد في تطبيق المصادقة ثم أدخل الرمز الناتج.',
  'auth.mfa.enrolSubmit': 'تفعيل ودخول',

  // --- Recovery codes ------------------------------------------------------
  'auth.recovery.title': 'رموز الاسترداد',
  'auth.recovery.subtitle':
    'احفظ هذه الرموز في مكان آمن. كل رمز يُستخدم مرة واحدة عند فقد جهاز المصادقة.',
  'auth.recovery.warning':
    'لن تُعرض هذه الرموز مرة أخرى. نحفظ بصماتها المشفّرة فقط، ولا يستطيع الدعم ولا مدير المنصة استرجاعها.',
  'auth.recovery.acknowledge': 'حفظت الرموز في مكان آمن وأدرك أنها لن تُعرض مجدداً.',
  'auth.recovery.acknowledgeFirst': 'أكّد حفظ الرموز أولاً',
  'auth.recovery.continue': 'متابعة إلى التطبيق',

  // --- Forgot password -----------------------------------------------------
  'auth.forgot.title': 'استعادة كلمة المرور',
  'auth.forgot.subtitle': 'أدخل بريدك الإلكتروني وسنرسل رابط إعادة تعيين صالحاً لمدة ٣٠ دقيقة.',
  'auth.forgot.submit': 'إرسال الرابط',
  'auth.forgot.needsEmail': 'أدخل البريد الإلكتروني',
  'auth.forgot.noConfirmation':
    'إن كان البريد مسجلاً لدينا فسيصلك الرابط. لا نؤكد وجود الحساب لأسباب أمنية.',
  'auth.forgot.backToLogin': 'العودة لتسجيل الدخول',
  'auth.forgot.sentTitle': 'تحقق من بريدك',
  'auth.forgot.sentBody': 'إن كان هذا البريد مسجلاً فسيصلك رابط إعادة التعيين خلال دقائق.',
  'auth.forgot.rateLimited': 'طلبات كثيرة. انتظر بضع دقائق ثم أعد المحاولة.',
  'auth.forgot.error': 'تعذّر إرسال الطلب. أعد المحاولة بعد قليل.',

  // --- Reset completion ----------------------------------------------------
  'auth.login.verifyRequired':
    'لم يتم تأكيد بريدك الإلكتروني بعد. افتح الرابط المُرسل إليك ثم أعد تسجيل الدخول.',
  'auth.login.verifyResend': 'إرسال رابط التأكيد مرة أخرى',
  'auth.login.verifyResent':
    'إن كان هذا البريد مسجلاً ويحتاج تأكيداً فسيصلك رابط جديد. تحقّق من بريدك ثم أعد تسجيل الدخول.',
  'auth.verify.title': 'تأكيد البريد الإلكتروني',
  'auth.verify.subtitle': 'اضغط الزر لتأكيد أن هذا البريد يخصّك.',
  'auth.verify.action': 'تأكيد البريد',
  'auth.verify.note':
    'لا يُستخدم الرابط إلا مرة واحدة، ولا نؤكّد البريد إلا عند ضغطك الزر — حتى لا تستهلكه برامج فحص الروابط في بريدك.',
  'auth.verify.doneTitle': 'تم تأكيد بريدك',
  'auth.verify.doneBody': 'يمكنك الآن تسجيل الدخول والمتابعة إلى مساحة العيادة.',
  'auth.verify.toLogin': 'الذهاب لتسجيل الدخول',
  'auth.verify.failedTitle': 'الرابط لم يعد صالحاً',
  'auth.verify.failedBody':
    'ينتهي رابط التأكيد خلال ٢٤ ساعة، ويُستخدم مرة واحدة فقط. أدخل بريدك لإرسال رابط جديد.',
  'auth.verify.noToken':
    'هذه الصفحة تُفتح من الرابط المُرسل في بريدك. أدخل بريدك لإرسال رابط جديد.',
  'auth.verify.emailLabel': 'البريد الإلكتروني',
  'auth.verify.resendAction': 'إرسال رابط جديد',
  'auth.verify.resendMaybe':
    'إن كان هذا البريد مسجلاً لدينا ويحتاج تأكيداً فسيصلك رابط جديد. لا نؤكد وجود الحساب لأسباب أمنية.',
  'auth.verify.backToLogin': 'العودة لتسجيل الدخول',
  'auth.reset.title': 'تعيين كلمة مرور جديدة',
  'auth.reset.subtitle': 'اختر كلمة مرور قوية. ستُنهى جميع جلساتك الحالية بعد الحفظ.',
  'auth.reset.newPassword': 'كلمة المرور الجديدة',
  'auth.reset.confirmPassword': 'تأكيد كلمة المرور',
  'auth.reset.mismatch': 'كلمتا المرور غير متطابقتين.',
  'auth.reset.rulesLabel': 'شروط كلمة المرور',
  'auth.reset.ruleLength': '{min} حرفاً على الأقل',
  'auth.reset.ruleLetter': 'حرف واحد على الأقل',
  'auth.reset.ruleDigit': 'رقم واحد على الأقل',
  'auth.reset.policyNotMet': 'كلمة المرور لا تحقق الشروط',
  'auth.reset.submit': 'حفظ كلمة المرور',
  'auth.reset.invalidToken': 'انتهت صلاحية الرابط أو استُخدم من قبل. اطلب رابطاً جديداً.',
  'auth.reset.error': 'تعذّر حفظ كلمة المرور. أعد المحاولة.',
  'auth.reset.doneTitle': 'تم تحديث كلمة المرور',
  'auth.reset.doneBody': 'يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة.',
  'auth.reset.sessionsRevoked': 'أُنهيت جميع الجلسات على الأجهزة الأخرى لحماية حسابك.',
  'auth.reset.goToLogin': 'تسجيل الدخول',
  'auth.reset.noTokenTitle': 'رابط غير صالح',
  'auth.reset.noTokenBody': 'هذا الرابط لا يحتوي على رمز إعادة تعيين. اطلب رابطاً جديداً.',
  'auth.reset.requestNew': 'طلب رابط جديد',

  // --- Idle timeout --------------------------------------------------------
  'auth.idle.title': 'ستُنهى الجلسة قريباً',
  'auth.idle.body':
    'لم نسجّل أي نشاط منذ فترة. ستُنهى الجلسة تلقائياً لحماية بيانات المرضى على هذا الجهاز.',
  'auth.idle.remaining': 'الوقت المتبقي',
  'auth.idle.stay': 'متابعة العمل',
  'auth.idle.extending': 'جارٍ تمديد الجلسة…',
  'auth.idle.signOutNow': 'تسجيل الخروج الآن',

  // --- Policy refusals (reason -> sentence, RBAC-011) ----------------------
  'auth.denied.missing_permission': 'لا تملك الصلاحية اللازمة لهذا الإجراء.',
  'auth.denied.branch_not_granted': 'هذا السجل يتبع فرعاً غير مُسند إليك.',
  'auth.denied.not_own_record': 'صلاحيتك مقيدة بسجلاتك الخاصة فقط.',
  'auth.denied.staff_suspended': 'تم إيقاف حسابك. راجع مدير النظام في منشأتك.',
  'auth.denied.staff_inactive': 'حسابك غير مُفعّل حالياً.',
  'auth.denied.not_a_member': 'ليس لديك عضوية في هذه المنشأة.',
  'auth.denied.tenant_suspended': 'حساب المنشأة موقوف. تبقى صفحات الفوترة والحساب متاحة.',
  'auth.denied.reauth_required': 'هذا إجراء حساس ويتطلب إعادة تأكيد كلمة المرور.',
  'auth.denied.idle_timeout': 'انتهت الجلسة بسبب عدم النشاط. سجّل الدخول مرة أخرى.',
};
